/**
 * AI CONTEXT: reusable-component - Giveaway slot grid for both modal and inline display
 * Journal: J-20250103-01
 * 
 * Purpose:
 * - Reusable slot grid component that can be used in both modal and inline contexts
 * - Shows available, taken, and user's own slots with proper visual indicators
 * - Handles slot selection and entry submission
 * - Maintains consistent styling and behavior across different use cases
 * 
 * Public API:
 * - <GiveawaySlotGrid 
 *     giveaway={giveaway} 
 *     user={user} 
 *     serverEntries={serverEntries}
 *     onSlotSelect={onSlotSelect}
 *     onEnterGiveaway={onEnterGiveaway}
 *     variant="modal" | "inline"
 *     maxHeight="60vh" | "200px"
 *     showEntryButton={true|false}
 *   />
 */

import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { authorizedFetch, isApiConfigured } from '../services/apiClient';
import { useGiveaway } from '../contexts/GiveawayContext';
import toast from 'react-hot-toast';
import { t } from '../i18n';

const GiveawaySlotGrid = ({ 
  giveaway, 
  user, 
  serverEntries = [], 
  onSlotSelect, 
  onEnterGiveaway,
  variant = 'modal', // 'modal' or 'inline'
  maxHeight = '60vh',
  showEntryButton = true,
  className = '',
  onSelectionChange, // callback to notify parent of selection changes
  winningSlot = null, // optional: if provided, highlight this slot distinctly
  readOnly = false, // if true, all slots are disabled and non-interactive
  initialTakenSlots = null, // optional: pre-fetched taken slots for immediate display
  onOpenModal = null, // callback to open the modal when no slots are selected and "Enter Giveaway" is clicked
  isSoldOut = false, // if true, show "winner" in legend for sold-out giveaways
  selectedSlots = undefined, // optional: controlled selected slots from parent (array of slot numbers)
  hideWinnerUntil = null // AI CONTEXT: admin-functionality - Hide winner until this timestamp (J-20260112-01)
}) => {
  const slotGridRef = useRef(null);
  const savedScrollTopRef = useRef(0);
  // AI CONTEXT: bug-fix - Use React state instead of ref for selected slots
  // Reasoning: Refs don't trigger re-renders, causing visual state to be lost on component re-render
  // Journal: J-20250103-02
  const [internalSelectedSlots, setInternalSelectedSlots] = useState(new Set());
  // Use controlled selectedSlots prop if provided, otherwise use internal state
  // Check for undefined (prop not provided) vs null/array (prop explicitly provided)
  const isControlled = selectedSlots !== undefined;
  const selectedSlotsSet = isControlled 
    ? new Set(Array.isArray(selectedSlots) ? selectedSlots : [])
    : internalSelectedSlots;
  const lockedTopRef = useRef(0);
  const lockScrollRef = useRef(false);
  const lockUntilRef = useRef(0);
  const suppressRestoreRef = useRef(false);
  const ignoreClicksUntilRef = useRef(0);
  const isDraggingRef = useRef(false);
  const touchStartYRef = useRef(0);
  const DRAG_THRESHOLD_PX = 10;
  // AI CONTEXT: performance - Use initial taken slots if provided for immediate display
  // Reasoning: Pre-fetched slots data prevents gray state on initial render
  // Journal: J-20251226-09
  const [takenSlotsFromServer, setTakenSlotsFromServer] = useState(initialTakenSlots || []);
  // Admin-only: leftover slots autofilled after close (customers did not purchase them)
  const [adminReservedSlots, setAdminReservedSlots] = useState([]);
  // Track if we've loaded slots data to show blank slots until data is ready
  const [slotsDataLoaded, setSlotsDataLoaded] = useState(!!initialTakenSlots);
  const { getUserCredits, enterGiveaway } = useGiveaway();
  const [announcedWin, setAnnouncedWin] = useState(null);

  // Fetch taken slots for accurate 'R' rendering (server returns all reserved slots)
  // AI CONTEXT: performance - Reduce API polling frequency and ensure immediate display
  // Reasoning: Reserved slots should show immediately, not after API delay
  // Journal: J-20251226-07, J-20251226-08, J-20251226-09
  // AI CONTEXT: bug-fix - REMOVED aggressive scroll restoration
  // Reasoning: Scroll restoration on every selection change caused snap-back behavior
  // Solution: Let browser handle natural scroll, only save position before state updates
  // Journal: J-20260111-02

  useEffect(() => {
    // If initialTakenSlots is provided, use it immediately (pre-fetched for modal)
    if (initialTakenSlots && Array.isArray(initialTakenSlots)) {
      setTakenSlotsFromServer(initialTakenSlots);
      setSlotsDataLoaded(true); // Mark as loaded if we have initial data
    }
  }, [initialTakenSlots]);

  useEffect(() => {
    let cancelled = false;
    let pollTimer = null;
    
    const fetchTakenSlots = async () => {
      try {
        if (!giveaway || !isApiConfigured() || cancelled) return;
        const resp = await authorizedFetch(`/giveaways/${giveaway.id}/slots`, { method: 'GET' });
        if (!cancelled && resp) {
          // Handle both array response and object with 'taken' property
          const taken = Array.isArray(resp) ? resp : (Array.isArray(resp.taken) ? resp.taken : []);
          const adminReserved = Array.isArray(resp?.adminReserved) ? resp.adminReserved : [];
          // Always set the state, even if empty, to ensure we use the most up-to-date data
          setTakenSlotsFromServer(taken);
          setAdminReservedSlots(adminReserved);
          setSlotsDataLoaded(true); // Mark as loaded once we get API response
        }
      } catch (_e) {
        // ignore; fallback to giveaway.entries below
        // If we have giveaway.entries, mark as loaded
        if (giveaway?.entries && Array.isArray(giveaway.entries) && giveaway.entries.length > 0) {
          setSlotsDataLoaded(true);
        }
      }
    };
    
    // Always fetch so adminReserved (admin-only) is populated when an admin is logged in
    fetchTakenSlots();
    
    // Poll every 15 seconds for real-time updates (reduced from 3 seconds)
    // This is sufficient for catching updates while significantly reducing API load
    // React Strict Mode in dev causes double mounting, but this won't happen in production
    pollTimer = setInterval(() => {
      if (!cancelled) {
        fetchTakenSlots();
      }
    }, 15000);
    
    return () => { 
      cancelled = true;
      if (pollTimer) clearInterval(pollTimer);
    };
  // We intentionally depend only on giveaway id for fetch
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giveaway?.id, initialTakenSlots]);

  // A11y announcement when a winning slot is provided/changes
  useEffect(() => {
    if (winningSlot && winningSlot !== announcedWin) {
      setAnnouncedWin(winningSlot);
    }
  }, [winningSlot, announcedWin]);

  // Sync internal state with controlled prop when it changes
  // Note: This is mainly for backwards compatibility - controlled mode uses prop directly
  useEffect(() => {
    if (isControlled && Array.isArray(selectedSlots)) {
      setInternalSelectedSlots(new Set(selectedSlots));
    }
  }, [isControlled, selectedSlots]);

  // AI CONTEXT: bug-fix - REMOVED scroll restoration useLayoutEffect
  // Reasoning: Scroll restoration was causing snap-back issues on mobile
  // Solution: Let browser handle scroll naturally - React preserves scroll during re-renders
  // Journal: J-20260112-05

  // AI CONTEXT: bug-fix - REMOVED aggressive scroll restoration that ran on every render
  // Reasoning: This caused scroll snap-back when selecting slots - browser handles scroll naturally
  // Journal: J-20260111-02

  if (!giveaway) return null;

  const allSlots = Array.from({ length: giveaway.totalSlots || 0 }, (_, i) => i + 1);
  
  // AI CONTEXT: bug-fix - Ensure proper giveawayId matching for user entries
  // Reasoning: User entries must be correctly filtered to highlight their slots in green
  // Constraints: Handle string comparison and ensure case-insensitive matching if needed
  // Journal: J-20251226-02
  const userEntriesFromServer = Array.isArray(serverEntries)
    ? serverEntries.filter(e => {
        // Normalize both IDs to strings for comparison
        const entryGiveawayId = String(e.giveawayId || '').trim();
        const currentGiveawayId = String(giveaway.id || '').trim();
        return entryGiveawayId === currentGiveawayId && entryGiveawayId !== '';
      })
    : [];
  const userEntries = userEntriesFromServer.length > 0
    ? userEntriesFromServer
    : giveaway.entries.filter(entry => entry.userId === user?.id);
  
  const normalizeSlotNum = (val) => {
    const n = Number(val);
    return Number.isFinite(n) ? n : null;
  };
  
  // AI CONTEXT: performance - Use both giveaway.entries and API data for immediate display
  // Reasoning: Reserved slots should show instantly, prioritizing API data when available
  // Journal: J-20251226-06, J-20251226-08
  // Start with giveaway.entries (available immediately for admin users)
  const entriesFromGiveaway = (giveaway.entries || [])
    .map(e => normalizeSlotNum(e.slotNumber ?? e.slot))
    .filter((n) => n !== null);
  
  // Use API data (takenSlotsFromServer) - this is fetched immediately on mount
  // For non-admin users, this is the primary source of reserved slot data
  const takenFromServer = (takenSlotsFromServer || [])
    .map(normalizeSlotNum)
    .filter((n) => n !== null);
  
  // Combine both sources - prioritize API data (more accurate) but include giveaway.entries as fallback
  // This ensures reserved slots show immediately from API, with giveaway.entries as backup
  // API data takes precedence because it's more up-to-date and includes all reservations
  let takenSlotsSet = new Set([...takenFromServer, ...entriesFromGiveaway]);
  
  // Always include user entries from server if they're not already in the set
  // This ensures user's own entries are always marked as taken (for green highlighting)
  if (userEntries && Array.isArray(userEntries) && userEntries.length > 0) {
    userEntries.forEach(e => {
      const slot = normalizeSlotNum(e.slotNumber ?? e.slot);
      if (slot !== null) {
        takenSlotsSet.add(slot);
      }
    });
  }
  
  const availableSlots = allSlots.filter(s => !takenSlotsSet.has(s));
  const mySlotsSet = new Set(
    (userEntries || [])
      .map(e => normalizeSlotNum(e.slotNumber ?? e.slot))
      .filter((n) => n !== null)
  );

  const adminFilledFromEntries = (giveaway.entries || [])
    .filter((e) => {
      const email = String(e.email || '').toLowerCase();
      return email === 'admin@tolberttactical.com' || e.name === 'Reserved by Admin' || e.tier === 'Reserved';
    })
    .map((e) => normalizeSlotNum(e.slotNumber ?? e.slot))
    .filter((n) => n !== null);
  const adminFilledSet = new Set([
    ...(adminReservedSlots || []).map(normalizeSlotNum).filter((n) => n !== null),
    ...adminFilledFromEntries,
  ]);
  const showAdminFilled = Boolean(user?.isAdmin);
  
  const credits = getUserCredits(user?.id);
  const creditKey = `${String(giveaway?.tier || 'Bronze').toLowerCase()}Credits`;
  const tierName = String(giveaway?.tier || 'Bronze');
  const defaultTierLimit = (tierName === 'Gold' || tierName === 'Platinum') ? 10 : 5;
  const maxPerEntry = (giveaway?.entryLimit ?? defaultTierLimit);

  // Stop scroll propagation for iOS
  const stopScrollPropagation = (event) => {
    event.stopPropagation();
  };

  const handleSlotClick = async (slotsArray) => {
    // If no slots selected and onOpenModal is provided, open the modal instead
    if (slotsArray.length === 0) {
      if (onOpenModal) {
        onOpenModal();
        return;
      }
      toast.error('Please select at least one slot'); 
      return; 
    }
    
    if (onEnterGiveaway) {
      const success = await onEnterGiveaway(giveaway.id, slotsArray, user.id);
      if (success) {
        // Reset selected slots (only if not controlled)
        if (!isControlled) {
          setInternalSelectedSlots(new Set());
        }
      }
    } else {
      // Fallback to context method
      const success = await enterGiveaway(giveaway.id, slotsArray, user.id);
      if (success) {
        // Reset selected slots (only if not controlled)
        if (!isControlled) {
          setInternalSelectedSlots(new Set());
        }
      }
    }
  };

  const gridClasses = variant === 'inline' 
    ? `grid grid-cols-5 gap-1 overflow-y-auto`
    : `grid grid-cols-5 gap-2 overflow-y-auto`;

  return (
    <div className={`${className}`}>
      {/* Header info for inline variant */}
      {variant === 'inline' && (
        <div className="mb-2">
          <p className="text-xs text-theme-silver-light">
            Choose up to {maxPerEntry} slots total • Your entries: {userEntries.length} • Available: {maxPerEntry - userEntries.length}
          </p>
          <p className="text-xs text-custom-orange">
            Credits: {credits[creditKey]} {giveaway.tier}
          </p>
        </div>
      )}

      {/* Slot grid */}
      <div
        ref={slotGridRef}
        data-giveaway-grid
        className={gridClasses}
        style={{ 
          maxHeight: variant === 'inline' ? '8rem' : maxHeight,
          WebkitOverflowScrolling: 'touch', 
          overscrollBehavior: 'contain', 
          touchAction: 'pan-y', 
          overflowAnchor: 'none', 
          scrollBehavior: 'auto', 
          contain: 'layout paint' 
        }}
        onTouchStart={(e) => {
          const t = e.touches && e.touches[0];
          touchStartYRef.current = t ? t.clientY : 0;
          isDraggingRef.current = false;
          if (slotGridRef.current) {
            savedScrollTopRef.current = slotGridRef.current.scrollTop;
          }
        }}
        onWheel={(e) => {
          savedScrollTopRef.current = e.currentTarget.scrollTop;
          stopScrollPropagation(e);
        }}
        onTouchMove={(e) => {
          const t = e.touches && e.touches[0];
          if (t && Math.abs(t.clientY - touchStartYRef.current) > DRAG_THRESHOLD_PX) {
            isDraggingRef.current = true;
          }
          savedScrollTopRef.current = e.currentTarget.scrollTop;
          stopScrollPropagation(e);
        }}
        onTouchEnd={() => {
          if (isDraggingRef.current) {
            ignoreClicksUntilRef.current = Date.now() + 400;
          }
          setTimeout(() => { isDraggingRef.current = false; }, 0);
        }}
        onScroll={(e) => {
          const now = Date.now();
          if (lockScrollRef.current && now < lockUntilRef.current) {
            e.currentTarget.scrollTop = lockedTopRef.current;
            return;
          }
          if (lockScrollRef.current && now >= lockUntilRef.current) {
            lockScrollRef.current = false;
          }
          savedScrollTopRef.current = e.currentTarget.scrollTop;
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {allSlots.map(slot => {
          const isMine = mySlotsSet.has(slot);
          const isAdminFilled = showAdminFilled && !isMine && adminFilledSet.has(slot);
          const isReserved = !isMine && !isAdminFilled && takenSlotsSet.has(slot);
          const selectable = !readOnly && !isReserved && availableSlots.includes(slot);
          // AI CONTEXT: admin-functionality - Hide winning slot if hideWinnerUntil is set and not yet reached
          // Reasoning: Respect hideWinnerUntil to control winner visibility
          // Journal: J-20260112-01
          const normalizedWinningSlot = winningSlot ? Number(winningSlot) : null;
          const isWinningSlot = normalizedWinningSlot !== null && normalizedWinningSlot === slot;
          const shouldHideWinner = hideWinnerUntil && Date.now() < hideWinnerUntil;
          const isWinning = isWinningSlot && !shouldHideWinner;
          // AI CONTEXT: bug-fix - Derive selected state from React state instead of ref
          // Reasoning: Visual state must be derived from state to persist across re-renders
          // Journal: J-20250103-02
          const isSelected = selectedSlotsSet.has(slot);
          
          // AI CONTEXT: ux - Always show slot numbers once data loads (reserved keeps number + reserved style)
          // Reasoning: Client request — see the number grid when slots are reserved, not just "R"
          // Journal: J-20251226-10
          const showContent = slotsDataLoaded || isMine || isAdminFilled || isWinning || isReserved || (giveaway?.entries && giveaway.entries.length > 0);
          const displayText = showContent || isWinning || isReserved || isMine || isAdminFilled
            ? slot
            : ''; // Blank until data loads
          
          return (
            <button
              key={slot}
              data-slot={slot}
              type="button"
              tabIndex={-1}
              style={{ overflowAnchor: 'none' }}
              onFocus={(e) => e.currentTarget.blur()}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); return false; }}
              onPointerDown={(e) => {
                e.preventDefault(); e.stopPropagation();
              }}
              onPointerUp={(e) => {
                e.preventDefault(); e.stopPropagation();
                if (readOnly) return; // All slots disabled in read-only mode
                if (isWinning) return; // winning slot is not interactive
                if (isMine) return; // cannot toggle your existing slots
                if (!selectable) return;
                if (isDraggingRef.current) return;
                
                // AI CONTEXT: bug-fix - Update React state instead of DOM manipulation
                // Reasoning: State updates trigger re-renders with correct visual state
                // Journal: J-20250103-02
                const wasSelected = selectedSlotsSet.has(slot);
                const newSelectedSet = new Set(selectedSlotsSet);
                
                if (wasSelected) {
                  newSelectedSet.delete(slot);
                } else {
                  // Check if adding this slot would exceed the maximum allowed slots
                  // Account for both existing entries and currently selected slots
                  const totalSlots = userEntries.length + newSelectedSet.size;
                  if (totalSlots >= maxPerEntry) { 
                    toast.error(`Maximum ${maxPerEntry} slots per entry (you have ${userEntries.length} existing entries)`); 
                    return; 
                  }
                  newSelectedSet.add(slot);
                }
                
                // Update state (only if not controlled)
                if (!isControlled) {
                  setInternalSelectedSlots(newSelectedSet);
                }
                
                // Notify parent of selection change (triggers parent re-render)
                const slotsArray = Array.from(newSelectedSet);
                if (onSelectionChange) {
                  onSelectionChange(slotsArray);
                }
                
                // AI CONTEXT: bug-fix - Let browser preserve scroll naturally, no manual restoration
                // Journal: J-20260112-05
              }}
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
              disabled={readOnly || isWinning || !selectable || isMine}
              aria-disabled={readOnly || isWinning || !selectable || isMine}
              title={isWinning ? (isMine ? 'Your winning slot 🏆' : 'Winning slot 🏆') : (isMine ? 'Your slot' : (isAdminFilled ? 'Admin filled' : (isReserved ? 'Reserved' : 'Available')))}
              className={`p-1 rounded border text-xs font-medium ${
                isWinning
                  ? 'bg-yellow-500 text-[#111] border-yellow-400 shadow-[0_0_0_2px_#111_inset,0_0_16px_rgba(234,179,8,0.8)] font-bold transition-colors duration-200'
                  : (isMine || isAdminFilled)
                    ? 'bg-green-600 text-white border-green-500 transition-colors duration-200'
                    : isReserved
                      ? 'bg-red-200/30 text-red-600 border-red-400/50 cursor-not-allowed' // No transition for reserved slots to prevent blinking
                      : isSelected
                        ? 'bg-custom-orange text-white border-custom-orange transition-colors duration-200' // Selected state from React state
                        : showContent
                          ? 'bg-glass-white text-theme-silver border-theme-silver/30 hover:border-custom-orange transition-colors duration-200'
                          : 'bg-glass-white/50 text-theme-silver/30 border-theme-silver/20' // Gray blank slot until data loads
              }`}
            >
              {displayText}
            </button>
          );
        })}
      </div>

      {/* A11y announcement for screen readers when a winning slot is provided */}
      <div className="sr-only" aria-live="polite">
        {announcedWin ? `Winning slot: ${announcedWin}` : ''}
      </div>

      {/* Entry button for inline variant */}
      {variant === 'inline' && showEntryButton && (
        <div className="mt-2">
          <button
            onClick={() => handleSlotClick(Array.from(selectedSlotsSet))}
            className="btn-primary w-full text-sm py-2"
            disabled={false} // Always enabled - if no slots selected and onOpenModal provided, opens modal
          >
            {selectedSlotsSet.size === 0 
              ? t('giveaways.enter')
              : `${t('giveaways.enter')} (${selectedSlotsSet.size} selected)`
            }
          </button>
        </div>
      )}

      {/* Legend for inline and modal variants */}
      {(variant === 'inline' || variant === 'modal') && (
        <div className="mt-1 text-xs text-theme-silver-light text-center">
          <span className="inline-flex items-center mr-2">
            <span className="w-2 h-2 inline-block rounded-sm mr-1 bg-green-600" /> Your slot
          </span>
          {showAdminFilled && (
            <span className="inline-flex items-center mr-2">
              <span className="w-2 h-2 inline-block rounded-sm mr-1 bg-green-600" /> Admin filled
            </span>
          )}
          <span className="inline-flex items-center mr-2">
            <span className="w-2 h-2 inline-block rounded-sm mr-1 bg-red-400/50" /> Reserved
          </span>
          <span className="inline-flex items-center mr-2">
            <span className="w-2 h-2 inline-block rounded-sm mr-1 bg-glass-white border border-theme-silver/30" /> Available
          </span>
          {/* AI CONTEXT: admin-functionality - Show "winner" in gold only if not hidden */}
          {/* Journal: J-20260112-01 */}
          {isSoldOut && winningSlot && (!hideWinnerUntil || Date.now() >= hideWinnerUntil) && (
            <span className="inline-flex items-center text-yellow-500">
              <span className="w-2 h-2 inline-block rounded-sm mr-1 bg-yellow-500" /> Winner
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default GiveawaySlotGrid;
